import { Button, Modal, Stack, UnstyledButton } from '@mantine/core';
import { IconInfoCircle, IconRosetteDiscountCheck } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDay } from '../../format.js';
import work from '../pms/PmsWork.module.css';
import classes from './Compliance.module.css';
import { useCertificateErrorText } from './errors.js';
import type { CertificateListItem } from './types.js';

type Pending = Pick<
  CertificateListItem,
  'id' | 'name' | 'number' | 'issuedBy' | 'issueDate' | 'expiryDate' | 'officeEntryPending'
>;

/**
 * Ofis onayı bekleyen gemide düzenlenen sertifikalar (ortağın denemesi 2026-10-23; ADR 0116 §2):
 * ofiste sayı ve her kayıtta "Review & approve"; gemide yalnız bilgi ("ofise onaya gönderildi").
 * Bekleyen yoksa çizilmez.
 */
export function IssuanceApprovalBanner({
  items,
  scope,
  canApprove,
  onOpen,
  onApprove,
}: {
  items: readonly Pending[];
  scope: 'web' | 'ship';
  canApprove: boolean;
  onOpen: (id: string) => void;
  onApprove: (id: string) => void;
}) {
  const { t } = useTranslation();
  const k = (key: string, values: Record<string, unknown> = {}) =>
    t(`vesselCertificates.approval.${key}`, values);
  const pending = items.filter((item) => item.officeEntryPending);
  if (pending.length === 0) return null;
  const office = scope === 'web';
  return (
    <section
      className={classes.approvalBand}
      aria-label={k('title')}
      data-scope={scope}
      data-testid="issuance-approval"
    >
      <p className={classes.approvalHead}>
        <IconRosetteDiscountCheck size={18} aria-hidden />
        <strong>
          {office
            ? k('officeTitle', { count: pending.length })
            : k('shipTitle', { count: pending.length })}
        </strong>
      </p>
      <ul className={classes.approvalList}>
        {pending.map((item) => (
          <li key={item.id} className={classes.approvalItem}>
            <button type="button" className={classes.approvalLink} onClick={() => onOpen(item.id)}>
              {item.name}
            </button>
            <span className={classes.approvalMeta}>
              {[item.number, item.issuedBy, formatDay(item.issueDate)].join(' · ')}
            </span>
            {office && canApprove ? (
              <Button size="xs" onClick={() => onApprove(item.id)}>
                {k('review')}
              </Button>
            ) : (
              <span className={classes.approvalMeta}>{office ? k('noRight') : k('shipNote')}</span>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * Onay penceresi (ADR 0116 §2): gemideki kaydın verisi; onayla ofis sertifikası yazılır, yerine
 * geçtiği sertifika "History"ye iner (silinmez). Tam süreli sertifikanın verisi farklıysa ofis
 * onaydan sonra sertifikayı düzenler.
 */
export function ApproveIssuanceDialog({
  opened,
  item,
  replaced,
  onClose,
  onApprove,
}: {
  opened: boolean;
  item: Pending | null;
  replaced: Pick<CertificateListItem, 'name' | 'number'> | null;
  onClose: () => void;
  onApprove: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const k = (key: string, values: Record<string, unknown> = {}) =>
    t(`vesselCertificates.approval.${key}`, values);
  const errorText = useCertificateErrorText();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  useEffect(() => {
    if (opened) setError(null);
  }, [opened]);
  const fact = (label: string, value: string) => (
    <div key={label}>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<span className={classes.drawerTitle}>{k('dialogTitle')}</span>}
      closeButtonProps={{ 'aria-label': t('vesselCertificates.dialogs.close') }}
    >
      {item ? (
        <Stack gap="sm">
          <dl className={classes.fields}>
            {fact(k('fields.name'), item.name)}
            {fact(k('fields.number'), item.number)}
            {fact(k('fields.issuedBy'), item.issuedBy)}
            {fact(k('fields.issueDate'), formatDay(item.issueDate))}
            {fact(k('fields.expiryDate'), item.expiryDate ? formatDay(item.expiryDate) : '—')}
            {replaced ? fact(k('fields.replaces'), `${replaced.name} · ${replaced.number}`) : null}
          </dl>
          <p className={classes.notice}>
            <IconInfoCircle size={16} aria-hidden />
            {replaced ? k('hintReplaces') : k('hint')}
          </p>
          {error ? (
            <p className={classes.formError} role="alert">
              {errorText(error)}
            </p>
          ) : null}
          <div className={classes.dialogActions}>
            <Button variant="default" onClick={onClose}>
              {t('vesselCertificates.dialogs.cancel')}
            </Button>
            <Button
              loading={busy}
              leftSection={<IconRosetteDiscountCheck size={16} />}
              onClick={async () => {
                setBusy(true);
                setError(null);
                try {
                  await onApprove();
                } catch (failure) {
                  setError(failure);
                } finally {
                  setBusy(false);
                }
              }}
            >
              {k('approve')}
            </Button>
          </div>
        </Stack>
      ) : null}
    </Modal>
  );
}

/** Ofis onayı bekleyen gemi kaydı (`GET /api/office/vessel-certificates/pending-issuances`). */
export interface PendingIssuanceNotice {
  id: string;
  vesselId: string;
  vesselName: string;
  name: string;
  number: string;
}

/** Vessel Certificates ekranında kaydın adresi (ofis): gemi ve seçili kayıt. */
export function issuancePath(item: Pick<PendingIssuanceNotice, 'id' | 'vesselId'>): string {
  const params = new URLSearchParams({ vessel: item.vesselId, cert: item.id });
  return `/compliance/vessel-certificates?${params.toString()}`;
}

/** Zilde ofis onayı bekleyen gemide düzenlenen sertifikalar (ADR 0116 §2); her biri sayılır. */
export function IssuanceBellList({
  items,
  onOpen,
}: {
  items: readonly PendingIssuanceNotice[] | null;
  onOpen: (item: PendingIssuanceNotice) => void;
}) {
  const { t } = useTranslation();
  if (!items || items.length === 0) return null;
  return (
    <ul className={work.bellList} aria-label={t('vesselCertificates.approval.title')}>
      {items.map((item) => (
        <li key={item.id}>
          <UnstyledButton className={work.bellItem} onClick={() => onOpen(item)}>
            <span className={work.bellIcon} aria-hidden>
              <IconRosetteDiscountCheck size={18} />
            </span>
            <span>
              <strong>{t('vesselCertificates.approval.bell')}</strong>
              <span className={work.note}>
                {[item.name, item.number, item.vesselName].filter(Boolean).join(' · ')}
              </span>
            </span>
          </UnstyledButton>
        </li>
      ))}
    </ul>
  );
}
